import i18n from 'i18next';

interface ErrorWithResponse {
  response?: { status?: number; data?: unknown };
}

export const FORBIDDEN_MESSAGE_FALLBACK =
  'Keine Berechtigung: Ihre Rolle erlaubt diese Aktion nicht. Wenden Sie sich an Ihre Administration, wenn Sie Zugriff benötigen.';

export function isForbiddenError(error: unknown): boolean {
  return (error as ErrorWithResponse | null | undefined)?.response?.status === 403;
}

export const FEATURE_NOT_ENABLED_FALLBACK =
  'Diese Funktion ist für Ihren Mandanten nicht aktiviert. Das liegt nicht an Ihrer Rolle. Wenden Sie sich an Ihre Administration, wenn Sie die Funktion nutzen möchten.';

const FEATURE_OFF_DETAIL = /not enabled|not activated|nicht aktiviert|nicht freigeschaltet/i;
const FEATURE_OFF_CODE = /(module|feature|plugin)_?(not_enabled|not_active|disabled)|^(module|feature)_/i;

export function forbiddenMessage(): string {
  if (!i18n.isInitialized) return FORBIDDEN_MESSAGE_FALLBACK;
  return String(i18n.t('errors.forbiddenAction', { defaultValue: FORBIDDEN_MESSAGE_FALLBACK }));
}

export function featureNotEnabledMessage(): string {
  if (!i18n.isInitialized) return FEATURE_NOT_ENABLED_FALLBACK;
  return String(i18n.t('errors.featureNotEnabled', { defaultValue: FEATURE_NOT_ENABLED_FALLBACK }));
}

/**
 * A 403 because the module or feature is switched off for the tenant (e.g.
 * "Feature not enabled for this tenant", TENDER_MODULE_NOT_ENABLED), which
 * no role change fixes.
 */
export function isFeatureNotEnabledError(error: unknown): boolean {
  if (!isForbiddenError(error)) return false;
  const data = (error as ErrorWithResponse).response?.data;
  if (!data || typeof data !== 'object') return false;
  const { detail, code } = data as { detail?: unknown; code?: unknown };
  const nested = detail && typeof detail === 'object' ? (detail as { code?: unknown; message?: unknown }) : undefined;
  const codes = [code, nested?.code].filter((value): value is string => typeof value === 'string');
  if (codes.some((value) => FEATURE_OFF_CODE.test(value))) return true;
  const text = typeof detail === 'string' ? detail : nested?.message;
  return typeof text === 'string' && FEATURE_OFF_DETAIL.test(text);
}

/** The readable text for a 403: switched-off feature or missing role. */
export function forbiddenMessageFor(error: unknown): string {
  return isFeatureNotEnabledError(error) ? featureNotEnabledMessage() : forbiddenMessage();
}

/**
 * The gateway's own reason for a failed request. FastAPI answers with
 * `detail` (a string, `{ message }` or a validation list); older routes use
 * `error` or `message`. Raw strings (proxy HTML pages) are ignored.
 */
export function apiErrorDetail(error: unknown): string | undefined {
  const data = (error as ErrorWithResponse | null | undefined)?.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const { detail, error: errorField, message } = data as {
    detail?: unknown;
    error?: unknown;
    message?: unknown;
  };
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item as { msg?: unknown } | null)?.msg)
      .filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0);
    if (messages.length > 0) return messages.join(', ');
  } else if (detail && typeof detail === 'object') {
    const nested = (detail as { message?: unknown }).message;
    if (typeof nested === 'string' && nested.trim()) return nested;
  }
  if (typeof errorField === 'string' && errorField.trim()) return errorField;
  if (typeof message === 'string' && message.trim()) return message;
  return undefined;
}

/** A 403 reads "Keine Berechtigung" (or "nicht aktiviert"); otherwise the server's reason, else the fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (isForbiddenError(error)) return forbiddenMessageFor(error);
  return apiErrorDetail(error) ?? fallback;
}

/** A 403 reads "Keine Berechtigung" (or "nicht aktiviert"); any other failure keeps the caller's (translated) text. */
export function forbiddenOr(error: unknown, fallback: string): string {
  return isForbiddenError(error) ? forbiddenMessageFor(error) : fallback;
}
